import { RefObject, useEffect } from 'react';

const MOBILE_QUERY = '(max-width: 600px)';
const TAB_SELECTOR = '.gc-dg-tab-link';
const ACTIVE_TAB_SELECTOR = '.gc-dg-tab-link.active';
const CARD_SELECTOR = '.gc-dg-payment-accordion';
// Keys the SDK uses to move between payment method tabs
const TAB_SELECTION_KEYS = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];

/**
 * @description On narrow screens the SDK scrolls to the top of the stacked payment cards when a
 * method opens, so this scrolls to the selected card instead.
 */
export const useSelectedMethodScroll = (panelRef: RefObject<HTMLElement | null>) => {
  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;

    const mediaQuery = window.matchMedia(MOBILE_QUERY);
    let frame = 0;

    // Next frame: after the SDK has opened the tab and started its own scroll
    const scrollToSelectedCard = () => {
      if (!mediaQuery.matches) return;
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const card = panel.querySelector(ACTIVE_TAB_SELECTOR)?.closest(CARD_SELECTOR);
        card?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    };

    const handleClick = (event: MouseEvent) => {
      if ((event.target as Element).closest(TAB_SELECTOR)) scrollToSelectedCard();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as Element;
      if (target.closest(TAB_SELECTOR) && TAB_SELECTION_KEYS.includes(event.key)) {
        scrollToSelectedCard();
      }
    };

    panel.addEventListener('click', handleClick);
    panel.addEventListener('keydown', handleKeyDown);

    return () => {
      cancelAnimationFrame(frame);
      panel.removeEventListener('click', handleClick);
      panel.removeEventListener('keydown', handleKeyDown);
    };
  }, [panelRef]);
};
